import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ServiceUnavailable from './ServiceUnavailable';

const API = process.env.REACT_APP_BACKEND_URL;
const HEALTH_CHECK_TIMEOUT = 5000; // 5 sekund timeout
const HEALTH_CHECK_INTERVAL = 30000; // 30 sekund

/**
 * BackendHealthCheck Component
 * 
 * Wrapper sprawdzający połączenie z backendem.
 * Jeśli backend jest niedostępny, wyświetla ServiceUnavailable.
 * 
 * Sprawdzanie odbywa się:
 * - Przy pierwszym załadowaniu (BLOKUJĄCE - strona nie renderuje się dopóki nie sprawdzi)
 * - Co 30 sekund w tle gdy backend jest niedostępny
 * - Po kliknięciu "Odśwież" przez użytkownika
 */
const BackendHealthCheck = ({ children }) => {
  // Domyślnie null = nie wiemy jeszcze czy backend działa
  const [isBackendAvailable, setIsBackendAvailable] = useState(null);
  const [isChecking, setIsChecking] = useState(true);
  const [hasChecked, setHasChecked] = useState(false);

  const checkHealth = useCallback(async () => {
    setIsChecking(true);
    try {
      // Prosty health check - sprawdzamy czy backend odpowiada
      const response = await axios.get(`${API}/api/health`, {
        timeout: HEALTH_CHECK_TIMEOUT,
        // Nie chcemy aby 401/403 było traktowane jako błąd połączenia
        validateStatus: () => true // Accept all status codes to check manually
      });
      
      // Sprawdź czy status jest OK (< 500)
      if (response.status >= 500) {
        console.warn('[HealthCheck] Backend unavailable, status:', response.status);
        setIsBackendAvailable(false);
        return false;
      }
      
      // Jeśli dostaliśmy odpowiedź (nawet 401), backend działa
      console.log('[HealthCheck] Backend available, status:', response.status);
      setIsBackendAvailable(true);
      return true;
    } catch (error) {
      // Każdy błąd = backend niedostępny
      console.warn('[HealthCheck] Backend unavailable:', error.message, error.code);
      setIsBackendAvailable(false);
      return false;
    } finally {
      setIsChecking(false);
      setHasChecked(true);
    }
  }, []);

  // Początkowe sprawdzenie
  useEffect(() => {
    checkHealth();
  }, [checkHealth]);

  // Okresowe sprawdzanie w tle (tylko gdy backend jest niedostępny)
  useEffect(() => {
    if (isBackendAvailable === false) {
      const interval = setInterval(checkHealth, HEALTH_CHECK_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [isBackendAvailable, checkHealth]);

  // Callback dla przycisku odświeżania
  const handleRetry = useCallback(() => {
    checkHealth().then((available) => {
      if (available) {
        // Backend dostępny - odśwież stronę aby załadować dane
        window.location.reload();
      }
    });
  }, [checkHealth]);

  // Podczas pierwszego sprawdzania pokazujemy loader
  if (isBackendAvailable === null || (isChecking && isBackendAvailable === null)) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400 text-sm">Łączenie z serwerem...</p>
        </div>
      </div>
    );
  }

  // Backend niedostępny - pokazujemy komunikat
  if (isBackendAvailable === false) {
    return <ServiceUnavailable onRetry={handleRetry} />;
  }

  // Wszystko OK - renderujemy aplikację
  return children;
};

export default BackendHealthCheck;
